import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import AttendLogo from '../assets/attendlogo.png';
import AuthShell, { authAlertSx, authButtonSx, authTextFieldSx } from '../components/AuthShell';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError(t('forgotPassword.emailRequired'));
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError(t('forgotPassword.emailInvalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword({ email });
      setSuccess(response.message || t('forgotPassword.resetEmailSent'));
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('forgotPassword.tooManyRequests'));
      } else {
        setError(err.response?.data?.message || t('forgotPassword.genericError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEmail(newValue);
    if (error) setError(null);
    if (emailError) validateEmail(newValue);
  };

  return (
    <AuthShell>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            px: 1.5,
            py: 1,
            borderRadius: 'var(--an-radius-md)',
            border: '1px solid var(--an-border-light)',
            backgroundColor: 'var(--an-surface)',
            boxShadow: 'var(--an-shadow-sm)',
          }}
        >
          <Box component="img" src={AttendLogo} alt="AttendNow" sx={{ height: 44, width: 'auto', display: 'block' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--an-radius-sm)',
              color: 'var(--an-primary)',
              backgroundColor: 'var(--an-primary-light)',
              flexShrink: 0,
            }}
          >
            <EmailIcon />
          </Box>
          <Typography variant="h4" component="h1" fontWeight={800} sx={{ color: 'var(--an-text-primary)' }}>
            {t('forgotPassword.title')}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'var(--an-text-secondary)', maxWidth: 440 }}>
          {t('forgotPassword.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={authAlertSx}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={authAlertSx}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('forgotPassword.emailLabel')}
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => validateEmail(email)}
          required
          margin="normal"
          error={!!emailError}
          helperText={emailError}
          disabled={loading || !!success}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || !!emailError || !!success}
          sx={{ ...authButtonSx, mb: 3 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('forgotPassword.sendResetLink')}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              fontWeight: 800,
              color: 'var(--an-primary)',
              '&:hover': {
                color: 'var(--an-primary-dark)',
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, mr: 1 }} />
            {t('forgotPassword.backToSignIn')}
          </Link>
        </Box>
      </Box>
    </AuthShell>
  );
};

export default ForgotPassword;
